/**
 * Normalizes time format to ensure consistent display
 * Converts between single-digit and double-digit formats
 * Examples: "3:00" -> "03:00", "03:00" -> "3:00", "12:00" -> "12:00"
 */
export const normalizeTimeFormat = (time: string): string => {
  if (!time || typeof time !== "string") return time;

  const parts = time.split(":");
  if (parts.length !== 2) return time;

  const [minutes, seconds] = parts;
  const normalizedMinutes = minutes.padStart(2, "0");
  const normalizedSeconds = seconds.padStart(2, "0");

  return `${normalizedMinutes}:${normalizedSeconds}`;
};

export const formatCountdownFromSeconds = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;

  return normalizeTimeFormat(`${minutes}:${String(remainingSeconds).padStart(2, "0")}`);
};

export const formatDurationFromSeconds = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.round(seconds));

  if (safeSeconds < 60) {
    return `${safeSeconds}s`;
  }

  const totalMinutes = Math.round(safeSeconds / 60);

  if (totalMinutes < 60) {
    return `${totalMinutes} min`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}m`;
};

export const formatDurationFromMinutes = (minutes: number): string => {
  const safeMinutes = Math.max(0, Math.round(minutes));

  if (safeMinutes < 60) {
    return `${safeMinutes} min`;
  }

  const hours = Math.floor(safeMinutes / 60);
  const remainingMinutes = safeMinutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}m`;
};

export const formatDurationBetween = (startTime: string, endTime?: string | null): string => {
  if (!endTime) {
    return "N/A";
  }

  const start = new Date(startTime);
  const end = new Date(endTime);
  const diffInSeconds = Math.floor((end.getTime() - start.getTime()) / 1000);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || diffInSeconds < 0) {
    return "N/A";
  }

  return formatDurationFromSeconds(diffInSeconds);
};
